// https://lucide.dev/icons/
import React from "react";
import { FlagSpain, FlagValencia, FlagUK } from "@/components/Flags";
import {
    Archive,
    Phone,
    Landmark,
    AlertCircle,
    AlertTriangle,
    ArrowDown,
    ArrowLeft,
    ArrowLeftRight,
    ArrowRight,
    ArrowUp,
    ArrowUpDown,
    Bell,
    Bot,
    Building,
    Building2,
    Calendar,
    Check,
    CheckCheck,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Briefcase,
    ClipboardCheck,
    Clock,
    Computer,
    Copy,
    Columns3,
    Download,
    ExternalLink,
    Eye,
    EyeOff,
    FileCode,
    FileText,
    Filter,
    FilterX,
    FolderGit2,
    FolderOpen,
    Globe,
    HardHat,
    Heart,
    House,
    Inbox,
    Info,
    KeyRound,
    KanbanSquare,
    Layers,
    LayoutTemplate,
    ListTodo,
    Loader2,
    Lock,
    Mail,
    Maximize2,
    Menu,
    MessageSquare,
    Minimize2,
    Minus,
    Monitor,
    MonitorSmartphone,
    Moon,
    MoreHorizontal,
    MoreVertical,
    Move,
    MoveDiagonal2,
    Paperclip,
    Pause,
    PenLine,
    Pencil,
    Plus,
    PlusCircle,
    Power,
    Repeat,
    Reply,
    RotateCcw,
    Save,
    Search,
    Send,
    SendHorizontal,
    Settings,
    Shield,
    ShieldAlert,
    ShieldCheck,
    Fingerprint,
    ScanFace,
    Sparkles,
    Server,
    Cpu,
    Sun,
    Trash2,
    Truck,
    UploadCloud,
    Unlock,
    User,
    UserShield,
    Users,
    UserCheck,
    UserPlus,
    X,
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    Bold,
    Code,
    Heading1,
    Heading2,
    Heading3,
    Highlighter,
    Image as ImageIcon,
    Indent,
    Italic,
    Link as LinkIcon,
    ListOrdered,
    Outdent,
    Palette,
    Quote,
    Redo,
    RemoveFormatting,
    Smile,
    Strikethrough,
    Subscript,
    Superscript,
    Table as TableIcon,
    Type,
    Underline,
    Undo,
    Unlink,
    Square,
    Mic,
    Signature,
    Play,
    GripVertical,
    Logs,
    ArrowLeft as Back,
    Coins,
    CircleEuro,
    Tally5,
    Share,
    Share2,
    Ban,
    Tag,
    Wrench,
    Database,
    RefreshCcw,
    BookOpen,
    Hammer,
    Factory,
    type LucideProps,
} from "lucide-react";

const PdfIcon: React.FC<LucideProps> = ({
    size = 24,
    className = "",
    ...props
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
    >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <text
            x="12"
            y="17"
            textAnchor="middle"
            fill="currentColor"
            stroke="none"
            fontSize="5.5"
            fontWeight="bold"
            letterSpacing="-0.2"
            fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
        >
            PDF
        </text>
    </svg>
);

const MarkdownIcon: React.FC<LucideProps> = ({
    size = 24,
    className = "",
    ...props
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
    >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <text
            x="12"
            y="17"
            textAnchor="middle"
            fill="currentColor"
            stroke="none"
            fontSize="5.5"
            fontWeight="bold"
            letterSpacing="-0.2"
            fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
        >
            MD
        </text>
    </svg>
);

export const ICON_MAP = {
    archive: Archive,
    phone: Phone,
    landmark: Landmark,
    tag: Tag,
    alert: AlertCircle,
    alertCircle: AlertCircle,
    alertTriangle: AlertTriangle,
    arrowDown: ArrowDown,
    arrowLeft: ArrowLeft,
    arrowLeftRight: ArrowLeftRight,
    arrowRight: ArrowRight,
    arrowUp: ArrowUp,
    factory: Factory,
    arrowUpDown: ArrowUpDown,
    bell: Bell,
    bot: Bot,
    building: Building,
    building2: Building2,
    calendar: Calendar,
    check: Check,
    checkCheck: CheckCheck,
    checkCircle: CheckCircle2,
    chevronDown: ChevronDown,
    chevronLeft: ChevronLeft,
    chevronRight: ChevronRight,
    chevronUp: ChevronUp,
    clipboardCheck: ClipboardCheck,
    clock: Clock,
    computer: Computer,
    copy: Copy,
    delete: Trash2,
    download: Download,
    edit: Pencil,
    externalLink: ExternalLink,
    eye: Eye,
    eyeOff: EyeOff,
    briefcase: Briefcase,
    fileCode: FileCode,
    fileText: FileText,
    pdf: PdfIcon,
    markdown: MarkdownIcon,
    md: MarkdownIcon,
    txt: FileText,
    html: FileCode,
    filter: Filter,
    filterOff: FilterX,
    filterX: FilterX,
    folder: FolderGit2,
    folderOpen: FolderOpen,
    FolderOpen,
    globe: Globe,
    hardHat: HardHat,
    home: House,
    house: House,
    inbox: Inbox,
    info: Info,
    kanban: KanbanSquare,
    kanbanSquare: KanbanSquare,
    key: KeyRound,
    keyRound: KeyRound,
    layers: Layers,
    layout: LayoutTemplate,
    layoutTemplate: LayoutTemplate,
    list: ListTodo,
    loader: Loader2,
    loading: Loader2,
    lock: Lock,
    mail: Mail,
    maximize: Maximize2,
    menu: Menu,
    message: MessageSquare,
    messageSquare: MessageSquare,
    minimize: Minimize2,
    minus: Minus,
    monitor: Monitor,
    resizeGrip: MoveDiagonal2,
    grip: GripVertical,
    gripVertical: GripVertical,
    moon: Moon,
    moreHorizontal: MoreHorizontal,
    moreVertical: MoreVertical,
    move: Move,
    paperclip: Paperclip,
    pause: Pause,
    penLine: PenLine,
    pencil: Pencil,
    plus: Plus,
    plusCircle: PlusCircle,
    power: Power,
    reload: RotateCcw,
    repeat: Repeat,
    reply: Reply,
    save: Save,
    columns3: Columns3,
    search: Search,
    send: Send,
    sendHorizontal: SendHorizontal,
    send_horizontal: SendHorizontal,
    settings: Settings,
    shield: Shield,
    shieldAlert: ShieldAlert,
    shieldCheck: ShieldCheck,
    fingerprint: Fingerprint,
    scanFace: ScanFace,
    sparkles: Sparkles,
    server: Server,
    cpu: Cpu,
    sun: Sun,
    trash: Trash2,
    truck: Truck,
    uploadCloud: UploadCloud,
    unlock: Unlock,
    user: User,
    users: Users,
    userPlus: UserPlus,
    userCheck: UserCheck,
    x: X,
    close: X,
    alignCenter: AlignCenter,
    alignJustify: AlignJustify,
    alignLeft: AlignLeft,
    alignRight: AlignRight,
    bold: Bold,
    code: Code,
    heading1: Heading1,
    heading2: Heading2,
    heading3: Heading3,
    highlighter: Highlighter,
    image: ImageIcon,
    indent: Indent,
    italic: Italic,
    link: LinkIcon,
    listOrdered: ListOrdered,
    outdent: Outdent,
    palette: Palette,
    quote: Quote,
    redo: Redo,
    removeFormatting: RemoveFormatting,
    smile: Smile,
    strike: Strikethrough,
    strikethrough: Strikethrough,
    subscript: Subscript,
    superscript: Superscript,
    table: TableIcon,
    type: Type,
    underline: Underline,
    undo: Undo,
    unlink: Unlink,
    // Flags
    flag_es: FlagSpain,
    "flag-es": FlagSpain,
    flag_spain: FlagSpain,
    es: FlagSpain,
    flag_ca: FlagValencia,
    "flag-ca": FlagValencia,
    flag_va: FlagValencia,
    "flag-va": FlagValencia,
    flag_valencia: FlagValencia,
    ca: FlagValencia,
    va: FlagValencia,
    valencia: FlagValencia,
    flag_en: FlagUK,
    "flag-en": FlagUK,
    flag_gb: FlagUK,
    "flag-gb": FlagUK,
    flag_uk: FlagUK,
    en: FlagUK,
    gb: FlagUK,
    square: Square,
    mic: Mic,
    signature: Signature,
    play: Play,
    "user-shield": UserShield,
    usershield: UserShield,
    gripvertical: GripVertical,
    logs: Logs,
    back: ArrowLeft,
    coins: Coins,
    "circle-euro": CircleEuro,
    "tally-5": Tally5,
    "key-round": KeyRound,
    share: Share,
    share2: Share2,
    ban: Ban,
    wrench: Wrench,
    database: Database,
    refresh: RefreshCcw,
    heart: Heart,
    bookopen: BookOpen,
    hammer: Hammer,
} as const;

export type IconName = keyof typeof ICON_MAP;

const NORMALIZED_ICON_MAP = Object.fromEntries(
    Object.entries(ICON_MAP).map(([key, component]) => [
        key.toLowerCase(),
        component,
    ])
) as Record<string, React.ElementType>;

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

export const ICON_SIZES: Record<IconSize, number> = {
    xs: 12,
    sm: 16,
    md: 24,
    lg: 32,
    xl: 40,
    "2xl": 48,
    "3xl": 64,
};

export const resolveIconSize = (size?: number | IconSize | string): number => {
    if (typeof size === "number") {
        return size;
    }

    if (typeof size === "string") {
        const normalized = size.trim().toLowerCase() as IconSize;
        if (normalized in ICON_SIZES) {
            return ICON_SIZES[normalized];
        }

        const parsed = Number(size);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }

    return ICON_SIZES.md;
};

export type IconColor =
    | "default"
    | "primary"
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "neutral";

export interface IconProps extends Omit<LucideProps, "ref" | "size" | "color"> {
    value?: IconName | string;
    name?: IconName | string;
    size?: number | IconSize;
    width?: number | string;
    height?: number | string;
    className?: string;
    color?: IconColor;
    background?: boolean;
}

const COLOR_BG_STYLES: Record<IconColor, string> = {
    default: "",
    primary: "bg-indigo-50 text-primary",
    success: "bg-green-50 text-green-600",
    warning: "bg-amber-50 text-amber-600",
    danger: "bg-red-50 text-red-600",
    info: "bg-blue-50 text-blue-600",
    neutral: "bg-gray-50 text-gray-600",
};

const COLOR_TEXT_STYLES: Record<IconColor, string> = {
    default: "",
    primary: "text-primary",
    success: "text-green-600",
    warning: "text-amber-600",
    danger: "text-red-600",
    info: "text-blue-600",
    neutral: "text-gray-600",
};

export const Icon: React.FC<IconProps> = ({
    value,
    name,
    size = 24,
    width,
    height,
    className = "",
    color = "default",
    background = false,
    style,
    ...props
}) => {
    if (React.isValidElement(value)) {
        return value as React.ReactElement;
    }

    if (React.isValidElement(name)) {
        return name as React.ReactElement;
    }

    const rawKey =
        typeof value === "string"
            ? value
            : typeof name === "string"
              ? name
              : "";
    if (!rawKey) {
        return null;
    }

    const iconKey = rawKey.trim().toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(NORMALIZED_ICON_MAP, iconKey)) {
        return null;
    }

    const Component = NORMALIZED_ICON_MAP[iconKey];
    if (!Component || typeof Component === "string") {
        return null;
    }

    // Safety guard against native browser constructors that throw "Illegal constructor"
    if (
        typeof Component === "function" &&
        /\{\s*\[native code\]\s*\}/.test(
            Function.prototype.toString.call(Component)
        )
    ) {
        return null;
    }

    const numericSize = resolveIconSize(size);
    const isFlag =
        iconKey.startsWith("flag") ||
        ["es", "ca", "va", "valencia", "en", "gb"].includes(iconKey);

    if (isFlag) {
        const flagWidth =
            width ?? (numericSize ? Math.round(numericSize * 1.5) : 24);
        const flagHeight = height ?? numericSize;
        return (
            <Component
                size={numericSize}
                width={flagWidth}
                height={flagHeight}
                className={className}
                style={style}
                aria-hidden="true"
                {...props}
            />
        );
    }

    const iconWidth = width ?? numericSize;
    const iconHeight = height ?? numericSize;

    const iconElement = (
        <Component
            size={numericSize}
            width={iconWidth}
            height={iconHeight}
            className={`shrink-0 inline-block align-middle ${!background && color !== "default" ? COLOR_TEXT_STYLES[color] : ""} ${!background ? className : ""}`.trim()}
            style={{ width: iconWidth, height: iconHeight, ...style }}
            aria-hidden="true"
            {...props}
        />
    );

    if (background) {
        const paddingClass = numericSize >= 32 ? "p-4" : "p-2";
        return (
            <div
                className={`inline-flex items-center justify-center ${paddingClass} rounded-full ${COLOR_BG_STYLES[color]} ${className}`.trim()}
            >
                {iconElement}
            </div>
        );
    }

    return iconElement;
};

export default Icon;
